import React, { useEffect, useState } from 'react';
import {
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    CircularProgress,
    Alert
} from '@mui/material';
import { styled } from '@mui/system';
import { FaDownload, FaDatabase, FaServer, FaShieldAlt } from 'react-icons/fa';
import AppColors from '../../utils/AppColors';
import { getSystemLogs, backupDatabase, getSystemHealth } from '../../services/api';

const SystemManagement = () => {
    const [backupLoading, setBackupLoading] = useState(false);
    const [systemHealth, setSystemHealth] = useState(null);
    const [logs, setLogs] = useState([]);

    useEffect(() => {
        const fetchSystemHealth = async () => {
            try {
                const profileData = await getSystemHealth();
                setSystemHealth(profileData);

                // Fetch logs only if profile data is available
                if (profileData) {
                    const response = await getSystemLogs();
                    setLogs(response.data.logs || []);
                }
            } catch (error) {
                console.error('Error fetching system health or logs:', error);
            }
        };

        fetchSystemHealth();
    }, []);

    const handleBackup = async () => {
        setBackupLoading(true);
        try {
            const response = await backupDatabase();
            // Handle successful backup (e.g., show a success message)
            console.log('Backup successful:', response.data);
        } catch (error) {
            console.error('Error creating backup:', error);
        } finally {
            setBackupLoading(false);
        }
    };

    return (
        <Container maxWidth="lg">
            <Typography variant="h4" gutterBottom>
                System Management
            </Typography>

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                System Health
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Status: {systemHealth?.status || 'N/A'}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Last Scan: {systemHealth?.security_last_scan || 'N/A'}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Actions Section */}
            <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    System Actions
                </Typography>
                <Grid container spacing={2}>
                    <Grid item>
                        <Button
                            variant="contained"
                            startIcon={<FaDownload />}
                            onClick={handleBackup}
                            disabled={backupLoading}
                            sx={{
                                backgroundColor: AppColors.primaryDark,
                                '&:hover': { backgroundColor: AppColors.primaryYellow, color: AppColors.primaryDark }
                            }}
                        >
                            {backupLoading ? <CircularProgress size={20} /> : 'Create Backup'}
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* System Logs */}
            <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>
                    System Logs
                </Typography>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Timestamp</TableCell>
                                <TableCell>Level</TableCell>
                                <TableCell>Message</TableCell>
                                <TableCell>Source</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {logs.map((log, index) => (
                                <TableRow key={index}>
                                    <TableCell>
                                        {new Date(log.timestamp).toLocaleString()}
                                    </TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={log.level} 
                                            sx={{ 
                                                backgroundColor: log.level === 'error' ? `${AppColors.errorRed}20` : 
                                                                log.level === 'warning' ? `${AppColors.warningOrange}20` : 
                                                                `${AppColors.successGreen}20`,
                                                color: log.level === 'error' ? AppColors.errorRed : 
                                                       log.level === 'warning' ? AppColors.warningOrange : 
                                                       AppColors.successGreen
                                            }} 
                                        />
                                    </TableCell>
                                    <TableCell>{log.message}</TableCell>
                                    <TableCell>{log.source || 'System'}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Container>
    );
};

export default SystemManagement;